/* eslint-disable no-mixed-operators */
/* eslint-disable eqeqeq */
import React, { useEffect, useState } from "react";
import "./producer-slider.css";
import crossIcon from "../../../assets/close.png";
import { useParams } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { changeSlide } from "../../../graphql/mutations";
import { onSlideChange, onSlideControlToggle } from "../../../graphql/subscriptions";
import * as config from "../../../aws-exports";
import { fetchPollResults } from "../../../services/event/pollResults";
import UpdateCurrentSlide from "../../../services/event/update-current-slide";
import CpeCredit from "../../cpe-credit/cpe-credit";
import "../../../components/cpe-credit/cpe-credit.css";
import SlideModal from "../../slide-model/slide-model";
import Back from "../../../assets/angle-left-solid.svg";
import Next from "../../../assets/angle-right-solid.svg";


const client = generateClient(config);

interface Slide {
  id: string;
  type:
  | "image"
  | "pollQuestions"
  | "pollresult"
  | "video"
  | "video/mp4"
  | "cpeForm";
  sequence: number;
  url?: string;
  fileName?: string;
  pollId?: string;
  pollResults?: PollResult[];
}


interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}

interface SlidesDisplayProps {
  onSlideSelect: (sequence: number) => void;
  selectedSlideSequence: number;
  slidesData: Slide[];
  pollData: any;
  allPollResultData: PollResultData[];
  role: number;
}

const SlidesDisplay: React.FC<SlidesDisplayProps> = ({
  onSlideSelect,
  selectedSlideSequence,
  slidesData,
  pollData,
  allPollResultData,
  role,
}) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [slides, setSlides] = useState<Slide[]>(slidesData);
  const [, setCurrentSlide] = useState<Slide | null>(null);
  const [, setIsSubmitted] = useState<boolean>(false);
  const [selectedAnswer, setSelectedAnswer] = useState<Record<string, string>>(
    {}
  );
  const [currentSlideSequence, setCurrentSlideSequence] = useState<number>(1);
  const [slideControlEnabled, setSlideControlEnabled] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);


  // Mapping Poll Results
  const pollResultsMap: Record<string, PollResult[]> = {};
  pollData.forEach((poll: any, index: number) => {
    pollResultsMap[poll.pollId] = allPollResultData[index]?.results || [];
  });

  useEffect(() => {
    setSlides(slidesData);
    if (selectedSlideSequence === 0) {
      handleSlideChange(1, slidesData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidesData, selectedSlideSequence]);

  useEffect(() => {
    // Subscription for slide change
    const subscription = client.graphql({ query: onSlideChange });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const type = data.onSlideChange.type;

        if (
          type !== "image" &&
          type !== "pollQuestions" &&
          type !== "pollresult" &&
          type !== "video" &&
          type !== "video/mp4" &&
          type !== "cpeForm"
        ) {
          console.error(`Unexpected slide type: ${type}`);
          return;
        }

        const newSlide: Slide = {
          id: data.onSlideChange.id,
          url: data.onSlideChange.url,
          sequence: data.onSlideChange.sequence,
          type: type,
          pollId: data.onSlideChange.pollId,
          pollResults: data.onSlideChange.pollResults
            ? data.onSlideChange.pollResults.map((result: any) => ({
              option: result.option,
              percentage: result.percentage,
              voteCount: result.voteCount,
            }))
            : undefined,
        };

        if (newSlide && newSlide.id === eventId) {
          setCurrentSlide(newSlide);
          setIsSubmitted(false);
          onSlideSelect(newSlide.sequence);
          setIsSyncing(false);
          setCurrentSlideSequence(newSlide.sequence);
          if (
            newSlide.type === "pollresult" &&
            newSlide.pollResults &&
            newSlide.pollId
          ) {
            // Since we are not fetching results via API, we'll use the existing pollResultsMap
            setCurrentSlide({
              ...newSlide,
              pollResults: pollResultsMap[newSlide.pollId!],
            });
          }
        }
      },
      error: (error) => console.warn("Subscription error:", error),
    });

    return () => observer.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSlideSelect]);

  useEffect(() => {
    // Subscribe to slide control toggle
    const subscription = client.graphql({ query: onSlideControlToggle });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const controlData = data.onSlideControlToggle;
        if (controlData.eventid === eventId) {
          setSlideControlEnabled(controlData.enableSlideControl);
        }
      },
      error: (error) => console.warn("Subscription error:", error),
    });

    return () => observer.unsubscribe();
  }, [onSlideSelect, eventId]);


  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  // Next slide logic
  const handleNextSlide = () => {
    // Find the current index of the slide in the array
    const currentIndex = slides.findIndex((slide) => slide.sequence === currentSlideSequence);
    setIsSyncing(true);
    if (currentIndex !== -1) {
      let nextSlide = null;

      // If the current slide is a poll question, go to the poll result slide
      if (slides[currentIndex].type === "pollQuestions") {
        const pollResultSlide: Slide = {
          ...slides[currentIndex],
          type: "pollresult",
          sequence: slides[currentIndex].sequence + 0.1, // Decimal sequence for poll result
        };

        nextSlide = pollResultSlide;
      }
      // If the current slide is a poll result, go to the next slide in the array
      else if (slides[currentIndex].type === "pollresult") {
        nextSlide = slides.find(slide => Math.floor(slide.sequence) > Math.floor(currentSlideSequence));
      }
      // For any other type of slide, just move to the next slide
      else if (currentIndex < slides.length - 1) {
        nextSlide = slides[currentIndex + 1];
      }

      // Only change the slide if we found a valid next slide
      if (nextSlide) {
        handleSlideChange(nextSlide.sequence);
        setCurrentSlideSequence(nextSlide.sequence);
      } else {
        setIsSyncing(false);
        console.warn('No next slide found');
      }
    }
  };

  const handlePreviousSlide = () => {
    const currentIndex = slides.findIndex((slide) => slide.sequence === currentSlideSequence);
    setIsSyncing(true);
    if (currentIndex > 0) {
      let previousSlide = slides[currentIndex - 1];

      // If the current slide is a poll result, go back to the poll question
      if (slides[currentIndex].type === "pollresult") {
        previousSlide = slides.find(
          (slide) => slide.type === "pollQuestions" && slide.sequence === Math.floor(currentSlideSequence)
        ) || previousSlide; // Find the poll question slide, or fallback to the previous slide
      }

      // If the previous slide is a poll question, check if the poll result slide exists
      else if (previousSlide.type === "pollQuestions") {
        const pollResultSlide = slides.find(
          (slide) => slide.type === "pollresult" && slide.sequence === previousSlide.sequence + 0.1
        );

        // If the poll result exists, set it as the previous slide
        if (pollResultSlide) {
          previousSlide = pollResultSlide;
        }
      }

      // Ensure previousSlide is not undefined before proceeding
      if (previousSlide) {
        handleSlideChange(previousSlide.sequence);
        setCurrentSlideSequence(previousSlide.sequence);
      } else {
        setIsSyncing(false);
        console.warn('No valid previous slide found');
      }
    }
  };

  const handleSlideChange = async (sequence: number, slidesData?: Slide[]) => {
    if (role === 0 && !slideControlEnabled) {
      console.warn("Speaker is not allowed to change slides at this moment.");
      return;
    }

    const currentSlides = slidesData || slides;
    const selectedSlide = currentSlides.find((slide) => slide.sequence === sequence);

    if (!selectedSlide) {
      console.warn("Slide not found for sequence:", sequence);
      return;
    }

    onSlideSelect(sequence); // Update UI

    try {
      const id = eventId as string;
      const url = selectedSlide.url ?? "";
      const type = selectedSlide.type ?? "";
      const pollId = selectedSlide.pollId ?? "";
      let pollResults = pollId ? pollResultsMap[pollId] : [];

      // Fetch poll results if needed
      if (type === "pollresult" && pollId) {
        const pollResultsResponse = await fetchPollResults(pollId);
        pollResults = pollResultsResponse.data.results;
      }

      const slideDetails = { id, url, sequence, type, pollId, pollResults };
      await client.graphql({
        query: changeSlide,
        variables: slideDetails,
      });
      await UpdateCurrentSlide({ slide: slideDetails }, eventId);
    } catch (error) {
      console.error("Error changing slide:", error);
    }
  };

  // Render Poll Results
  const renderPollResults = (pollId: string) => {
    const results = pollResultsMap[pollId];

    if (!results || results.length === 0) {
      return <div>Loading poll results...</div>;
    }

    return (
      <div className="poll-result silder-poll-wrapper">
        {results.map((result) => (
          <div key={result.option} className="poll-result-item poll-silder-view">
            <div className="poll-option">{result.option}</div>
            <div className="poll-percentage">
              <div className="poll-bar" style={{ width: `${result.percentage}%`, backgroundColor: "#007bff" }}></div>
              <div className="poll-text">{result.percentage}% ({result.voteCount} votes)</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSlides = () => {
    return slides.map((slide: Slide) => {
      const poll = pollData.find((poll: any) => poll.pollId === slide.pollId);
      const slideComponents = [];

      if (slide.type !== "pollresult") {
        slideComponents.push(
          <div
            key={slide.sequence}
            className={`slide ${selectedSlideSequence === slide.sequence ? "selected" : ""}`}
            onClick={() => handleSlideChange(slide.sequence)}
            style={{ cursor: role === 0 && !slideControlEnabled ? "not-allowed" : "pointer" }}
          >
            <>
              {slide.type === "image" && (
                <div className="img-video-wrapper">
                  <img crossOrigin="anonymous" src={slide.url} alt={`Slide ${slide.sequence}`} />
                </div>
              )}
              {(slide.type === "video" || slide.type === "video/mp4") && (
                <div className="img-video-wrapper">
                  <video src={slide.url} muted style={{ width: "100%", height: "120px" }} />
                </div>
              )}
              {slide.type === "pollQuestions" && poll && (
                <div className="poll-question">
                  <h3 className="silder-poll-question-wrapper">
                    Q.{poll.questionTitle}
                  </h3>
                  <form>
                    {Object.entries(poll.options).map(([key, value]) => (
                      <div key={key}>
                        <input
                          type="radio"
                          id={key}
                          name={`question-${poll.pollId}`}
                          value={key}
                          checked={selectedAnswer[poll.pollId] === key}
                          onChange={() =>
                            setSelectedAnswer({
                              ...selectedAnswer,
                              [poll.pollId]: key,
                            })
                          }
                          disabled={role === 0 && !slideControlEnabled}
                        />
                        <label className="silder-option-wrapper" htmlFor={key}>
                          {String(value)}
                        </label>
                      </div>
                    ))}
                  </form>
                </div>
              )}
              {slide.type === "cpeForm" && (
                <CpeCredit className="file1-classname" />
                // </div>
              )}
              {selectedSlideSequence === slide.sequence && (
                <div className="live-icon">Live</div>
              )}
            </>
          </div>
        );

        if (slide.type === "pollQuestions" && poll) {
          const pollResultSlide: Slide = {
            id: `${slide.id}-result`,
            type: "pollresult",
            sequence: slide.sequence + 0.1,
            pollId: slide.pollId,
            url: "",
          };

          if (!slides.some((s) => s.sequence === pollResultSlide.sequence)) {
            slides.push(pollResultSlide);
          }

          slideComponents.push(
            <div
              key={pollResultSlide.id}
              className={`slide ${selectedSlideSequence === pollResultSlide.sequence ? "selected" : ""}`}
              onClick={() => handleSlideChange(pollResultSlide.sequence)}
              style={{ cursor: role == 0 && !slideControlEnabled ? "not-allowed" : "pointer", flex: "1 0 25%" }}
            >
              {renderPollResults(slide.pollId!)}
              {selectedSlideSequence === pollResultSlide.sequence && <div className="live-icon">Live</div>}
            </div>
          );
        }
      }

      return slideComponents;
    });
  };

  return (
    <div className="main-slider-wrapper">
      <div>
        <div style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <h6 style={{ marginRight: "15px" }}>Slide control</h6>
              <div className="slide-controls">
                <button
                  className="arrow-button"
                  onClick={handlePreviousSlide}
                  disabled={currentSlideSequence === 1 || (role === 0 && !slideControlEnabled)|| isSyncing}
                  style={{
                    cursor: role === 0 && !slideControlEnabled ? "not-allowed" : "pointer",
                  }}
                >
                  <img  className="left-arrow" src={Back} alt="back-arrow" />
                  {/* <span className="left-arrow">{"<"}</span> */}
                </button>
                <button
                  className="arrow-button"
                  onClick={handleNextSlide}
                  disabled={
                    currentSlideSequence === slides.length || (role === 0 && !slideControlEnabled || isSyncing)
                  }
                  style={{
                    cursor: role === 0 && !slideControlEnabled ? "not-allowed" : "pointer",
                  }}
                >
                  <img  className="right-arrow" src={Next} alt="back-arrow" />
                  {/* <span className="right-arrow">{">"}</span> */}
                </button>
              </div>

            </div>
            <button className="view-slide-button" onClick={handleOpenModal}>
            Preview Slides
            </button>
          </div>
          {isModalOpen && <SlideModal slides={slides} handleCloseModal={handleCloseModal} />}
          <div>
            <img src={crossIcon} alt="Close icon" style={{ marginRight: "20px", width: "13px", height: "13px", cursor: "pointer", display: "none" }} />
          </div>
        </div>
        <hr style={{ marginTop: "0", marginBottom: "0" }} />
      </div>
      <div className="slides-container">{renderSlides()}</div>
    </div>
  );
};

export default SlidesDisplay;
